// Drive-Backup (Phase 8): verbinden laeuft ueber einen echten Redirect zu
// Google (siehe seiten/Backup.tsx), alles andere ueber die API.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.js";

export interface BackupStatus {
  verbunden: boolean;
  connectedAt: string | null;
  lastBackupAt: string | null;
}

export function useBackupStatus() {
  return useQuery({
    queryKey: ["backup-status"],
    queryFn: () => api<BackupStatus>("/backup/drive/status"),
  });
}

export interface DriveDatei {
  id: string;
  name: string;
  createdTime: string;
  size?: string;
}

export function useBackupListe(aktiv: boolean) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["backup-liste"],
    enabled: aktiv,
    // Kein automatischer Retry: Ein entzogener Zugriff faellt serverseitig
    // beim ersten Versuch auf, schaltet die Verbindung ab und liefert beim
    // zweiten (automatischen) Versuch dann "verbunden: false" - ohne Fehler,
    // aber mit irrefuehrend leerer Liste statt der eigentlichen Ursache.
    retry: false,
    queryFn: async () => {
      try {
        return (await api<{ backups: DriveDatei[] }>("/backup/drive")).backups;
      } catch (fehler) {
        // status stimmt sonst nicht mehr mit der Wahrheit ueberein: der
        // Server hat die Verbindung bei diesem Fehler bereits abgeschaltet.
        void queryClient.invalidateQueries({ queryKey: ["backup-status"] });
        throw fehler;
      }
    },
  });
}

export function useBackupJetzt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<DriveDatei>("/backup/drive/run", { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["backup-status"] });
      void queryClient.invalidateQueries({ queryKey: ["backup-liste"] });
    },
  });
}

export interface WiederherstellungsZeile {
  collectionId: number;
  collectionName: string;
  importiert: number;
  bereitsVorhanden: number;
  unbekannt: string[];
}

export function useBackupWiederherstellen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, targets }: { fileId: string; targets: number[] }) =>
      api<{ sammlungen: WiederherstellungsZeile[] }>("/backup/drive/restore", {
        method: "POST",
        body: { fileId, targets },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["entries"] }),
  });
}

export function useBackupTrennen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/backup/drive", { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["backup-status"] });
      void queryClient.invalidateQueries({ queryKey: ["backup-liste"] });
    },
  });
}
